import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { CalendarIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';

const DueDateChangeReport = () => {
  const [dueDateChanges, setDueDateChanges] = useState([]);
  const [filteredChanges, setFilteredChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('all'); // 'all', 'week', 'month'
  const [showCompleted, setShowCompleted] = useState(false); // Toggle for completed/canceled projects

  useEffect(() => {
    const fetchDueDateChanges = async () => {
      try {
        setLoading(true);
        
        // Use the correct endpoint format that matches the server route
        // If period is 'all', no specific period filter is needed
        let url = '/api/notes/due-date-changes';
        if (period !== 'all') {
          url += `/${period}`;
        } else {
          // For 'all', we'll use 'month' with a very large number to get all records
          url += '/month';
        }
        
        const response = await axios.get(url);
        setDueDateChanges(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching due date changes:', err);
        setError('Failed to load due date changes. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDueDateChanges();
  }, [period]);

  // Filter changes based on project status
  useEffect(() => {
    if (!dueDateChanges.length) {
      setFilteredChanges([]);
      return;
    }

    const filtered = dueDateChanges.filter(change => {
      // If showCompleted is true, show all changes
      if (showCompleted) return true;

      // If not a project, always show the change (could be a step)
      if (change.entity_type !== 'project') return true;

      // Check project status
      const status = change.entity_status ? change.entity_status.toLowerCase() : '';
      
      // Don't show completed or canceled projects
      return !status.includes('complete') && 
             !status.includes('done') && 
             !status.includes('finished') && 
             !status.includes('cancel');
    });

    setFilteredChanges(filtered);
  }, [dueDateChanges, showCompleted]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM d, yyyy');
  };
  
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  // Create a link for an entity
  const renderEntityLink = (change) => {
    if (!change || !change.entity_id || !change.entity_type) {
      return change.entity_name || 'Unknown';
    }
    
    switch (change.entity_type) {
      case 'project':
        return (
          <a 
            href={`/projects/${change.entity_id}`}
            className="text-indigo-600 hover:text-indigo-900"
          >
            {change.entity_name}
          </a>
        );
      case 'step':
        return (
          <a 
            href={`/steps/${change.entity_id}`}
            className="text-indigo-600 hover:text-indigo-900"
          >
            {change.entity_name}
          </a>
        );
      default:
        return change.entity_name || 'Unknown';
    }
  };

  // Extract due date change info from note content
  const parseDueDateInfo = (content) => {
    if (!content) return { previous_date: null, new_date: null, reason: '' };
    
    // Example format: "Due date changed from 2023-01-01 to 2023-02-01. Reason: Project scope expanded"
    try {
      const fromMatch = content.match(/from (\S+) to/);
      const toMatch = content.match(/to (\S+)\./);
      const reasonMatch = content.match(/Reason: (.*?)$/);
      
      return {
        previous_date: fromMatch && fromMatch[1] !== 'not set' ? fromMatch[1] : null,
        new_date: toMatch && toMatch[1] !== 'removed' ? toMatch[1] : null,
        reason: reasonMatch ? reasonMatch[1] : '',
      };
    } catch (err) {
      console.error('Error parsing due date change content:', err);
      return { previous_date: null, new_date: null, reason: '' };
    }
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">Due Date Change Report</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            A record of all due date changes across projects, phases, and steps.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Checkbox for showing completed/canceled projects */}
          <div className="flex items-center">
            <input
              id="show-completed"
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="show-completed" className="ml-2 block text-sm text-gray-700">
              Show completed projects
            </label>
          </div>
          
          {/* Period filters */}
          <div className="flex space-x-2">
            <button
              onClick={() => handlePeriodChange('all')}
              className={`px-3 py-1 text-sm font-medium rounded ${
                period === 'all' 
                  ? 'bg-indigo-100 text-indigo-700' 
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              All time
            </button>
            <button
              onClick={() => handlePeriodChange('week')}
              className={`px-3 py-1 text-sm font-medium rounded ${
                period === 'week' 
                  ? 'bg-indigo-100 text-indigo-700' 
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              Past week
            </button>
            <button
              onClick={() => handlePeriodChange('month')}
              className={`px-3 py-1 text-sm font-medium rounded ${
                period === 'month' 
                  ? 'bg-indigo-100 text-indigo-700' 
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              Past month
            </button>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="py-10 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="py-8 px-4 text-center text-red-500">
          {error}
        </div>
      ) : filteredChanges.length === 0 ? (
        <div className="py-8 px-4 text-center text-gray-500">
          No due date changes found in the selected time period.
        </div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {filteredChanges.map((change) => (
            <li key={change.id}>
              <div className="block hover:bg-gray-50">
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {renderEntityLink(change)}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex">
                      <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {change.entity_type ? change.entity_type.charAt(0).toUpperCase() + change.entity_type.slice(1) : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <p className="flex items-center text-sm text-gray-500">
                        Changed from: <span className="ml-1 font-medium">
                          {formatDate(parseDueDateInfo(change.content).previous_date)}
                        </span>
                      </p>
                      <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                        <ChevronRightIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                        To: <span className="ml-1 font-medium">
                          {formatDate(parseDueDateInfo(change.content).new_date)}
                        </span>
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                      <p>
                        Changed by: <span className="font-medium">{change.user_name || 'Unknown user'}</span> on {formatDate(change.created_at)}
                      </p>
                    </div>
                  </div>
                  {parseDueDateInfo(change.content).reason && (
                    <div className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      <span className="font-medium">Reason: </span>
                      {parseDueDateInfo(change.content).reason}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DueDateChangeReport; 